import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'

import { REPORT_DATASET_COLUMNS, REPORT_DATASET_LABELS, type ReportColumnDef } from '@/api/reports'
import { NotLivePage, useNotLivePage } from '@/app/notLive'
import { Card, DataTable, EmptyState, Icon, LoadingBlock, type Column } from '@/design'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import { buildCsv } from './csv'
import { useReportRows, useSavedReport } from './hooks'
import './reports.css'

function downloadCsv(filename: string, csv: string): void {
  // Excel on Windows ignores the charset in the MIME type and needs a BOM to
  // read the file as UTF-8, or currency symbols like "£" render as mojibake.
  const blob = new Blob(['\uFEFF', csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

/** A report's rows as table columns, over whichever keys the row picker has enabled. */
function tableColumns(defs: readonly ReportColumnDef[]): Column<Record<string, string | number>>[] {
  return defs.map((def) => ({ key: def.key, header: def.header, align: def.align, cell: (row) => row[def.key] ?? '' }))
}

/** One saved report: its rows over the current global filters, a column picker, and CSV export of what's on screen. */
export function ReportDetailPage() {
  const notLive = useNotLivePage('savedReport')
  const { id } = useParams<{ id: string }>()
  const report = useSavedReport(id ?? '')
  const dataset = report.data?.data?.dataset
  const rows = useReportRows(dataset)
  const search = useFilterSearch()

  // Column-picker overrides, keyed by report id: a report with no entry here
  // falls back to its own saved columns, so switching reports (a param change,
  // not a remount) never carries over a stale selection from the last one.
  const [overridesById, setOverridesById] = useState<Record<string, string[]>>({})

  if (notLive) return <NotLivePage />

  if (report.isPending) return <LoadingBlock rows={6} />

  if (report.isError) {
    return (
      <Card>
        <EmptyState variant="outage" description="This report could not be loaded." />
      </Card>
    )
  }

  if (!report.data?.data) {
    return (
      <Card>
        <EmptyState
          variant="no-results"
          title="Report not found"
          description="It may have been removed, or the link is out of date."
          action={
            <Link className="ci-button" to={{ pathname: '/reports', search }}>
              Back to reports
            </Link>
          }
        />
      </Card>
    )
  }

  const saved = report.data.data
  const allColumns = REPORT_DATASET_COLUMNS[saved.dataset]
  const activeKeys = overridesById[saved.id] ?? saved.columns
  const activeColumns = allColumns.filter((column) => activeKeys.includes(column.key))
  // Rows have no stable id of their own; the position in the current, filtered result is stable enough for a table key.
  const data = (rows.data?.data ?? []).map((row, index) => ({ ...row, __rowId: String(index) }))

  function toggle(key: string) {
    setOverridesById((current) => {
      const base = current[saved.id] ?? saved.columns
      const next = base.includes(key) ? base.filter((candidate) => candidate !== key) : [...base, key]
      return { ...current, [saved.id]: next }
    })
  }

  return (
    <div className="ci-stack">
      <Card
        title={saved.name}
        subtitle={`${REPORT_DATASET_LABELS[saved.dataset]} · owned by ${saved.owner}`}
        actions={
          <div className="rp-export-row">
            <button
              type="button"
              className="ci-button"
              data-variant="primary"
              disabled={data.length === 0 || activeColumns.length === 0}
              onClick={() => downloadCsv(`${saved.name}.csv`, buildCsv(activeColumns, data))}
            >
              <Icon name="reports" size={14} /> Export CSV
            </button>
            <button type="button" className="ci-button" data-variant="ghost" disabled title="PDF export is coming soon">
              PDF (coming soon)
            </button>
          </div>
        }
      >
        <div className="rp-column-picker" role="group" aria-label="Visible columns">
          {allColumns.map((column) => (
            <label key={column.key}>
              <input type="checkbox" checked={activeColumns.includes(column)} onChange={() => toggle(column.key)} />
              {column.header}
            </label>
          ))}
        </div>

        {rows.isPending ? <LoadingBlock rows={6} /> : null}
        {rows.isError ? <EmptyState variant="outage" compact description="This report's rows could not be loaded." /> : null}
        {rows.data ? (
          <DataTable
            caption={saved.name}
            columns={tableColumns(activeColumns)}
            rows={data}
            rowKey={(row) => row.__rowId as string}
            empty={<EmptyState variant="no-data" compact title="No rows for the current filters" />}
          />
        ) : null}
      </Card>
    </div>
  )
}
